'use client';

/**
 * @fileoverview Shopping cart page UI: Shopify Storefront checkout (`shopify-buy`) + Nest sync.
 *
 * **Dynamic-pricing brands** (`isDynamicPricingBrand` in `@/config/features`):
 * - **Login required** — guest checkout state is not loaded; users see a sign-in prompt.
 * - Line items must carry `_nest_product_id`, `_nest_brand`, `_nest_product_type` (set on PDP
 *   `AddToCartIsland` when `isDynamicPricing` is true). Those attributes are **identity
 *   only**; charged amounts are computed on `POST /api/draftorder`.
 * - **Checkout** calls `/showroom/api/draftorder` with the full cart, then redirects to
 *   `invoiceUrl` (Draft Order checkout), not `checkout.webUrl`.
 * - **Order subtotal display** is hydrated from `POST /api/products/pricing` using the same
 *   Nest ids — never trust variant list price alone for the member line total.
 *
 * **Standard brands:** Checkout continues to use Shopify hosted checkout `checkoutState.webUrl`.
 * Legacy optional `_target_price` on line items (old Function/sign path) is still honored for
 * subtotal display when not a dynamic-pricing brand.
 *
 * @see docs/SHOPIFY_INTEGRATION.md
 */
import React, { useEffect, useRef, useState } from "react"
import { useRouter } from "next/navigation";
import LineItem from "./LineItem"
import { useShopify } from "@/hooks/useShopify";
import { CartInfo, fetchCartDetails, getUserCartInfo, getUserInfo, updateCartData } from "@/utils/useUserInfo";
import { CART_TOKEN, GCART_TOKEN, deepEqual, gtmClickTracking, navigateAndReplaceHistory, navigateWithHistory } from "@/utils";
import { useAuth } from "@nestingale/auth/hooks";
import cssStyles from "./shopify_cart.module.scss";
import Button from '@mui/material/Button';
import { useSearchParams } from 'next/navigation'
import Campaign from "@/store/campaign";
import { getProductsByIds } from "@/lib/api/productApi";
import InventoryAlert from "./unavailableItems";
import { isDynamicPricingBrand } from '@/config/features';

/** Custom attribute keys — must stay in sync with `AddToCartIsland` dynamic path. */
const ATTR_NEST_PRODUCT_ID = '_nest_product_id';
const ATTR_NEST_BRAND = '_nest_brand';
const ATTR_NEST_PRODUCT_TYPE = '_nest_product_type';

/**
 * Maps Storefront checkout line items to the `/api/draftorder` request body.
 *
 * @returns `null` if any line is missing required metadata (user must re-add from PDP).
 *          Each object matches server `DraftLineItem` shape.
 */
function lineItemsToDraftPayload(lineItems: any[] | undefined): Record<string, unknown>[] | null {
	if (!lineItems?.length) return null;
	const out: Record<string, unknown>[] = [];
	for (const item of lineItems) {
		const attrs = item.customAttributes ?? [];
		const productId = attrs.find((a: any) => a.key === ATTR_NEST_PRODUCT_ID)?.value;
		const brandVal = attrs.find((a: any) => a.key === ATTR_NEST_BRAND)?.value;
		const productTypeVal = attrs.find((a: any) => a.key === ATTR_NEST_PRODUCT_TYPE)?.value;
		const variantGid = item.variant?.id;
		if (!productId || !variantGid) return null;
		out.push({
			variantGid,
			quantity: item.quantity ?? 1,
			productId,
			brand: brandVal || undefined,
			productType: productTypeVal || undefined,
		});
	}
	return out;
}

/**
 * Cart shell used by `[vendor]/cart/page.tsx`.
 *
 * @param props.brand — Path segment for “continue shopping” links (e.g. `"/gtr"` or `""`).
 * @param props.vendor — Route vendor slug (e.g. `gtr`, `nestingale`) for {@link isDynamicPricingBrand}.
 */
const ShopifyCart: React.FC<any> = (props) => {

	let [userInfo, setUserInfo] = useState(null as any);
	let [isUserInfoError, setUserInfoError] = useState(null as any);
	const brand = props.brand || "";
	const vendorSlug: string = props.vendor ?? "nestingale";
	console.log("[CART] vendorSlug", vendorSlug);
	const isDynamicBrand = isDynamicPricingBrand(vendorSlug);
	console.log("[CART] isDynamicBrand", isDynamicBrand);
	/** `productId` → pricing object from our Shopify-backed proxy for order summary UI. */
	const [dynamicPriceMap, setDynamicPriceMap] = useState<Record<string, { price: string; comparePrice: string }>>({});
	const checkoutId = useRef(null);
	const isGuestCheckoutCreated = useRef(false);
	const [isVariantsAdded, setIsVariantsAdded] = useState(false);
	const [cartItem, setCartItem] = useState(null);
	const isCartItemDeleted = useRef(false);
	const router = useRouter();
	const searchParams = useSearchParams()
	const moveToCheckout = useRef(searchParams.get('next') as string === "checkout" ? true : false);
	const [updateToInventoryQuantity, setUpdateToInventoryQuantity] = useState(false);
	const [cartMerging, setCartMerging] = useState(false);
	const [loggedInUserUpdating, setLoggedInUserUpdating] = useState(false);
	const [canInitiateCartMerge, setCanInitiateCartMerge] = useState(false);
	const [cartMerged, setCartMerged] = useState(false);
	const [productData, setProductData] = useState([]);
	const emailSavedRef = useRef(''); // tracks 'checkoutId:userId' to avoid saveEmail feedback loop
	const cartLoadAttempted = useRef(false); // ensures cart is loaded only once after userInfo is DONE
	const [isRedirecting, setIsRedirecting] = useState(false);

	const {
		getClient,
		cartStatus,
		createNewCart,
		dbAddressInfo,
		cartCount,
		fetchProducts,
		skusCommaSeperated,
		checkoutState,
		guestCheckoutState,
		createCheckout,
		addVariant,
		setCount,
		fetchShippingAddressInShopify,
		saveShippingAddressInShopify,
		saveEmailAddressInShopify
	} = useShopify();
	const { userEmail } = useAuth();

	console.log('[CART] render:', { cartCount, lineItems: checkoutState?.lineItems?.length, cartStatus, hasUserInfo: !!userInfo, cartMerging, cartMerged });

	useEffect(() => {
		if (skusCommaSeperated) {
			(async () => {
				const productData = await getProductsByIds({ productIds: skusCommaSeperated });
				console.log('[CART] productData loaded:', { count: productData?.products?.length, skus: skusCommaSeperated });
				setProductData(productData?.products?.map((eachProduct: any) => {
					return {
						productImage: eachProduct.productImage,
						availabilityCount: eachProduct.availabilityCount,
						productId: eachProduct.productId,
						productName: eachProduct.productName
					}
				}))
			})();
		}
	}, [checkoutState])

	/**
	 * Dynamic brands: fetch display prices from the Shopify Admin API via our proxy.
	 * This ensures the /cart exactly matches the /draftorder checkout logic ($10 reduction).
	 */
	useEffect(() => {
		console.log("[CART] Dynamic Brand useEffect", { isDynamicBrand, userInfo, checkoutState, dynamicPriceMap, userEmail });
		if (!isDynamicBrand || typeof window === 'undefined') return;
		if (!userEmail) {
			setDynamicPriceMap({});
			return;
		}
		const lines = checkoutState?.lineItems;
		if (!lines?.length) {
			setDynamicPriceMap({});
			return;
		}

		// Use Admin Store Domain from env, fallback to checkout host
		const adminDomain = process.env.SHOPIFY_ADMIN_STORE_DOMAIN || (typeof window !== 'undefined' ? window.location.hostname : '');
		const variantGids = lines.map((li: any) => li.variant?.id).filter(Boolean);

		if (variantGids.length === 0) {
			setDynamicPriceMap({});
			return;
		}

		let cancelled = false;
		(async () => {
			try {
				const res = await fetch('/showroom/api/shopify/cart-pricing', {
					method: 'POST',
					credentials: 'same-origin',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						shop: adminDomain,
						variantGids
					}),
				});
				if (!res.ok) return;
				const data = await res.json();
				const pricing = data.pricing ?? {};
				const merged: Record<string, { price: string; comparePrice: string }> = {};

				// Map by productId (_nest_product_id) for compatibility with UI rendering
				for (const li of lines) {
					const pid = li.customAttributes?.find((a: any) => a.key === ATTR_NEST_PRODUCT_ID)?.value;
					const variantId = li.variant?.id;
					const itemPricing = pricing[variantId];
					if (pid && itemPricing) {
						merged[pid] = {
							price: String(itemPricing.price),
							comparePrice: String(itemPricing.comparePrice)
						};
					}
				}

				if (!cancelled) setDynamicPriceMap(merged);
			} catch (e) {
				console.warn('[CART] shopify price fetch failed', e);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [checkoutState?.lineItems, isDynamicBrand]);

	useEffect(() => {
		const userInfoPropValue = props.userInfo;
		if (userInfoPropValue) {
			setUserInfo((prevUserInfo: any) => {
				if (deepEqual(prevUserInfo, userInfoPropValue)) {
					return prevUserInfo;
				}
				return userInfoPropValue;
			});
		}
	}, [props.userInfo]);

	function upatedIfDeletedCartItem(value: boolean = false) {
		isCartItemDeleted.current = value;
	}

	async function updateCartInfoIfloggedIn() {
		try {
			const userInfo = await getUserInfo() as any;
			if (userInfo && userInfo.cId && userEmail && !loggedInUserUpdating) {
				setLoggedInUserUpdating(true);
				setCanInitiateCartMerge(false);
				const cartData: CartInfo = await getUserCartInfo(userInfo);
				console.log('[CART] getUserCartInfo result:', { cartId: cartData?.cartId, error: cartData?.error });
				if (cartData) {
					if (cartData.error) {
						return setUserInfoError(true)
					}
					try {
						await updateCartData(userInfo, checkoutState, { cartItem, setCartItem, overwrite: true, deletedItem: isCartItemDeleted.current, updatedIfDeletedCartItem: upatedIfDeletedCartItem, createNewCart });
					} catch (e) {
						console.log("Failed Saving the cartData", e)
					}

					setUserInfo((prevUserInfo: any) => {
						if (deepEqual(prevUserInfo, userInfo)) {
							return prevUserInfo;
						}
						return userInfo;
					});
					setCartCookie(cartData.cartId);
					isGuestCheckoutCreated.current = false;
				}
				setLoggedInUserUpdating(false);
				setCanInitiateCartMerge(true);
			}
		} catch (e) {
			console.log("Failed Getting the userInfo", e)
		}
		return null;
	}

	useEffect(() => {
		async function doIt() {
			if (checkoutState && Object.keys(checkoutState).length > 0 && userEmail) {
				console.log('[CART] checkoutState updated, calling updateCartInfoIfloggedIn:', { id: checkoutState?.id, lineItems: checkoutState?.lineItems?.length });
				await updateCartInfoIfloggedIn();
			}
		}
		doIt();
	}, [checkoutState])

	useEffect(() => {
		console.log("🛒 [Cart API v3.0] Cart navigation check:", {
			hasCheckoutState: checkoutState && Object.keys(checkoutState).length > 0,
			isLoggedIn: !!userEmail,
			cartMerged: cartMerged,
			webUrl: checkoutState?.webUrl,
			moveToCheckout: moveToCheckout.current
		});
		if (checkoutState && Object.keys(checkoutState).length > 0 && userEmail && cartMerged) {
			console.log("🛒 [Cart API v3.0] Cart merged successfully, clearing guest cart cookie");
			emptyGuestCartCookie();
			if (moveToCheckout.current) {
				console.log("🛒 [Cart API v3.0] Navigating to checkout:", checkoutState.webUrl);
				setTimeout(() => {
					navigateWithHistory(router, checkoutState.webUrl);
				}, 100);
			}
		}
	}, [checkoutState, cartMerged])

	useEffect(() => {
		if (checkoutState?.id && userInfo?.cId) {
			const key = `${checkoutState.id}:${userInfo.cId}`;
			if (emailSavedRef.current === key) return; // already sent for this checkout+user — skip to break feedback loop
			emailSavedRef.current = key;
			(async () => {
				try {
					saveEmailAddressInShopify({ checkoutId: checkoutState.id, emamilAddress: userEmail })
					await fetchShippingAddressInShopify({ customerId: userInfo.cId });
				} catch (e) { }
			})()
		}
	}, [checkoutState?.id, userInfo?.cId])

	useEffect(() => {
		if (dbAddressInfo && checkoutState && Object.keys(checkoutState).length > 0) {
			saveShippingAddressInShopify({ checkoutId: checkoutState.id, shippingAddress: dbAddressInfo })
		}
	}, [dbAddressInfo])

	useEffect(() => {
		if (isVariantsAdded) {
			fetchProducts();
		}
	}, [isVariantsAdded])

	useEffect(() => {
		// Wait for full userInfo resolution before any cart loading.
		// 'DONE' means we know if user is logged-in (has cId) or guest (no cId).
		if (userInfo?.status !== 'DONE') return;
		// Only run the cart load once — prevents re-running when parent re-passes the same userInfo
		if (cartLoadAttempted.current) return;
		cartLoadAttempted.current = true;

		console.log('[CART] userInfo DONE — starting cart load:', { cId: userInfo?.cId });

		// Dynamic-pricing vendors: no guest cart — avoid creating/fetching Storefront checkout until login.
		if (isDynamicBrand && !userEmail) {
			console.log('[CART] Dynamic pricing brand — skipping cart load until user signs in');
			return;
		}

		(async () => {
			// Only call the DB for logged-in users
			let cartData = userInfo?.cId ? await getUserCartInfo(userInfo) : null;
			if (typeof window !== 'undefined') {
				//@ts-ignore
				let loggedInUserCartId = cartData?.cartId || window.getCookie(CART_TOKEN);
				//@ts-ignore
				let guestCartId = window.getCookie(GCART_TOKEN);

				loggedInUserCartId = (loggedInUserCartId == "null" || loggedInUserCartId == "undefined") ? null : loggedInUserCartId;
				guestCartId = (guestCartId == "null" || guestCartId == "undefined") ? null : guestCartId;

				console.log('[CART] cart IDs resolved — loggedIn:', loggedInUserCartId, 'guest:', guestCartId);

				if (guestCartId && loggedInUserCartId) {
					isGuestCheckoutCreated.current = true;
					// AWAIT before main cart load to prevent concurrent useMutation race
					await createCheckout({ checkoutId: guestCartId, guest: true });
				}
				//@ts-ignore
				checkoutId.current = loggedInUserCartId || guestCartId;
			}
			await createCheckout({ checkoutId: checkoutId.current });
			fetchProducts();
		})()
	}, [userInfo, isDynamicBrand])

	useEffect(() => {
		const emailAddress = userEmail;
		if (emailAddress && isGuestCheckoutCreated.current) {
			if ((window as any).getCookie(GCART_TOKEN) == (window as any).getCookie(CART_TOKEN)) {
				console.log("----- Shopify-Cart guest token and cart token are the same, clearing guest token cookie--------");
				emptyGuestCartCookie();
			} else {
				console.log("----- Shopify-Cart merging guest cart to logged in cart--------");
				(async () => {
					await mergeGuestCartToLoggedInCart();
				})()
			}
		}
	}, [guestCheckoutState])

	useEffect(() => {
		console.log('[CART] cartStatus/checkoutState effect:', { cartStatus, lineItems: checkoutState?.lineItems?.length, cartCount })

		async function getCount() {
			let lineItems =
				checkoutState.lineItems && checkoutState.lineItems.length > 0
					? checkoutState.lineItems
					: []
			let count = 0
			lineItems.forEach((item: { quantity: number }) => {
				count += item.quantity
				return count
			})
			setCount(count);
		}
		if (checkoutState) {
			getCount();
		}
	}, [cartStatus, checkoutState])

	function continueShopping(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
		e.preventDefault();
		window.location.replace(`/showroom${brand}/rooms`);
	}

	function mergeGuestCartToLoggedInCart() {
		if (guestCheckoutState && guestCheckoutState.lineItems && !cartMerging && canInitiateCartMerge) {
			if (guestCheckoutState.lineItems.length === 0) {
				// Nothing to merge — clear stale guest cookie and mark merged
				console.log('[CART] mergeGuestCartToLoggedInCart: guest cart is empty, clearing GCART_TOKEN');
				emptyGuestCartCookie();
				setCartMerged(true);
				return;
			}
			setCartMerging(true);
			const guestLineItems = guestCheckoutState.lineItems;
			const guestLineItemsToAdd = guestLineItems.map((item: any) => {
				if (item.variant.id != "no variant Id") {
					return {
						variantId: item.variant.id,
						quantity: item.quantity
					}
				}
			});
			addVariant(checkoutId.current, guestLineItemsToAdd, async () => {
				emptyGuestCartCookie()
				setIsVariantsAdded(true);
				console.log("success in adding all the variants of guest to loggedIn user checkout");
			});
			setCartMerging(false);
			setCartMerged(true);
		}
	}

	function emptyGuestCartCookie() {
		if (typeof window !== 'undefined') {
			//@ts-ignore
			window.setCookieWithoutSecure(GCART_TOKEN, null, 0);
		}
	}

	function setCartCookie(cartId: string | undefined) {
		if (typeof window !== 'undefined') {
			cartId = (cartId != undefined) ? cartId : checkoutState.id
			//@ts-ignore
			window.setCookieWithoutSecure(CART_TOKEN, cartId, 10);
		}
	}

	/**
	 * Primary checkout handler.
	 * - **Dynamic brand:** `POST /showroom/api/draftorder` → full-page navigation to `invoiceUrl`.
	 * - **Otherwise:** legacy delay then navigate to Storefront `checkoutState.webUrl`.
	 */
	async function openCheckout(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
		e.preventDefault();
		console.log("🛒 [Cart API v3.0] Checkout button clicked:", {
			cartId: checkoutState?.id,
			webUrl: checkoutState?.webUrl,
			isLoggedIn: !!userEmail,
			skusCommaSeperated
		});
		gtmClickTracking({ event: 'check_out_clicked', 'productId': skusCommaSeperated });

		const emailAddress = userEmail;
		if (!emailAddress) {
			console.log("🛒 [Cart API v3.0] User not logged in, redirecting to login");
			return navigateAndReplaceHistory(router, `/login?next=${encodeURIComponent(window.location.href + `?next=checkout`)}`);
		}

		if (isDynamicBrand) {
			const lineItems = lineItemsToDraftPayload(checkoutState?.lineItems);
			if (!lineItems || lineItems.length === 0) {
				alert(
					'Cart items are missing product metadata for checkout. Remove items and add them again from the product page.',
				);
				return;
			}
			try {
				setIsRedirecting(true);
				const res = await fetch('/showroom/api/draftorder', {
					method: 'POST',
					credentials: 'same-origin',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ lineItems }),
				});
				const data = await res.json().catch(() => ({}));
				if (!res.ok) {
					setIsRedirecting(false);
					console.error('[CART] Draft Order checkout failed', data);
					alert(typeof data.error === 'string' ? data.error : 'Checkout failed. Please try again.');
					return;
				}
				if (data.invoiceUrl) {
					// Polled Redirect Strategy:
					// 1. Wait ~2s to allow Shopify backend to finish indexing the Draft Order session.
					// 2. Append ?checkout=true to push the user directly into the checkout flow.
					console.log("🛒 [Cart API v3.0] Draft Order created, waiting for indexing...");
					setTimeout(() => {
						const url = new URL(data.invoiceUrl);
						url.searchParams.set('checkout', 'true');
						console.log("🛒 [Cart API v3.0] Redirecting to checkout:", url.toString());
						window.location.href = url.toString();
					}, 2000);
					return;
				}
				setIsRedirecting(false);
				alert('Checkout failed — no checkout URL returned.');
			} catch (err) {
				setIsRedirecting(false);
				console.error('[CART] Draft Order checkout error', err);
				alert('Checkout failed. Please try again.');
			}
			return;
		}

		setIsRedirecting(true);
		setTimeout(() => {
			console.log("🛒 [Cart API v3.0] Proceeding to checkout:", checkoutState.webUrl);
			return navigateWithHistory(router, checkoutState.webUrl);
		}, 5000);
	}

	/* Dynamic-pricing vendor + anonymous visitor: block cart UI (no guest cart flow). */
	if (userInfo?.status === 'DONE' && isDynamicBrand && !userEmail) {
		return (
			<div id="cart">
				<Campaign campaignName="cart" pageName="cart" />
				<header className={cssStyles['Cart__header']}>
					<div className={cssStyles['empty-cart-container']}>
						<h2>Sign in required</h2>
						<p>This storefront requires an account to use the cart and checkout.</p>
						<Button
							className={cssStyles['Cart_Continue_shopping']}
							variant="contained"
							color="primary"
							onClick={() =>
								navigateAndReplaceHistory(
									router,
									`/login?next=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`,
								)
							}
						>
							Sign in
						</Button>
					</div>
				</header>
			</div>
		);
	}

	if (cartCount <= 0) {
		return (<div id="cart">
			<Campaign campaignName="cart" pageName="cart" />
			{
				(userInfo || userInfo?.unauthorized || isUserInfoError) ?
					<header className="Cart__header">
						<div className={cssStyles["empty-cart-container"]}>
							{
								(!checkoutState || (checkoutState && checkoutState.lineItems &&
									checkoutState.lineItems.length <= 0)) ?
									<>
										<h2>Empty Cart</h2>
										<Button className={cssStyles["Cart_Continue_shopping"]} variant="contained" color="primary"
											onClick={(e: any) => continueShopping(e)}>
											Continue Shopping
										</Button>
									</> :
									<>Loading...</>
							}
						</div>
					</header>
					: <>Loading...</>
			}
		</div>)
	}

	return (
		<div id="cart">
			<Campaign campaignName="cart" pageName="cart" />
			<div className={`Cart Cart--open`}>
				<header className={cssStyles['Cart__header']}>
					<h2 >Your cart</h2>
					<InventoryAlert updateToInventoryQuantity={updateToInventoryQuantity} setUpdateToInventoryQuantity={setUpdateToInventoryQuantity} productData={productData} checkoutState={checkoutState} />
				</header>
				<div className={cssStyles["cart-summary-n-footer"]}>
					<div className={cssStyles["Cart__line-items"]}>
						<LineItem updateToInventoryQuantity={updateToInventoryQuantity} setCartCookie={setCartCookie} productData={productData} cartItem={cartItem} isCartItemDeleted={isCartItemDeleted} userInfo={userInfo} setCartItem={setCartItem} upatedIfDeletedCartItem={upatedIfDeletedCartItem} dynamicPriceMap={dynamicPriceMap} isDynamicBrand={isDynamicBrand} />
					</div>
					<div className={cssStyles["Cart__footer"]}>
						<h2>Order Summary</h2>
						{(() => {
							/* Member-aware subtotal: dynamic brands use server pricing map; others may use legacy `_target_price`. */
							const totals = checkoutState.lineItems?.reduce((acc: any, item: any) => {
								let unitPrice: number;
								let catalogPrice: number;

								if (isDynamicBrand) {
									const pid = item.customAttributes?.find((a: any) => a.key === ATTR_NEST_PRODUCT_ID)?.value;
									const fromMap = pid && dynamicPriceMap[pid];
									unitPrice = fromMap != null ? parseFloat(fromMap.price) : parseFloat(item.variant?.price?.amount ?? '0');
									// In dynamic mode, the "Catalog Price" for comparison should be the MSRP (comparePrice) from our map
									catalogPrice = fromMap != null ? parseFloat(fromMap.comparePrice) : parseFloat(item.variant?.price?.amount ?? '0');
								} else {
									const targetAttr = item.customAttributes?.find((a: any) => a.key === '_target_price');
									unitPrice = targetAttr ? parseFloat(targetAttr.value) : parseFloat(item.variant?.price?.amount ?? '0');
									catalogPrice = parseFloat(item.variant?.price?.amount ?? '0');
								}

								const qty = item.quantity ?? 1;
								acc.memberSubtotal += unitPrice * qty;
								acc.catalogSubtotal += catalogPrice * qty;
								return acc;
							}, { memberSubtotal: 0, catalogSubtotal: 0 }) ?? { memberSubtotal: 0, catalogSubtotal: 0 };

							const { memberSubtotal, catalogSubtotal } = totals;
							const hasMemberPricing = Math.abs(memberSubtotal - catalogSubtotal) > 0.001;
							return (
								<div className={cssStyles["cart-all-items-info"]}>
									<span className={cssStyles["subtext-key"]}>Order Subtotal:</span>
									<span className="Cart-info__pricing">
										{hasMemberPricing ? (
											<>
												<span style={{ textDecoration: 'line-through', marginRight: '6px' }}>$ {catalogSubtotal.toFixed(2)}</span>
												<span className="pricing">$ {memberSubtotal.toFixed(2)}</span>
											</>
										) : (
											<span className="pricing">$ {catalogSubtotal.toFixed(2)}</span>
										)}
									</span>
								</div>
							);
						})()}
						<div className={cssStyles["cart-all-items-info"]}>
							<span className={cssStyles["subtext-key"]}>Shipping:</span>
							<span className="Cart-info__pricing">
								<span className="pricing">Free!</span>
							</span>
						</div>
						<div className={cssStyles["cart-all-items-info"]}>
							<span className={cssStyles["subtext-key"]}>Taxes:</span>
							<span className="Cart-info__pricing">
								<span className="pricing">Calculated on checkout page</span>
							</span>
						</div>
						<div className={cssStyles["cart-all-items-info"]}>
							<span className={cssStyles["subtext-key"]}>Order Total:</span>
							<span className="Cart-info__pricing">
								<span className="pricing">Calculated on checkout page</span>
							</span>
						</div>
						<Button 
							id="check_out_clicked" 
							className={cssStyles['Cart__checkout']} 
							variant="contained" 
							color="primary"
							disabled={isRedirecting}
							onClick={(e: any) => openCheckout(e)}
						>
							{isRedirecting ? 'Processing...' : 'Checkout'}
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}

export default ShopifyCart;
